import { z } from "zod";
import { newPasswordSchema } from "./auth";
import { emailSchema, INDIAN_STATES, stateCodeSchema } from "./platform";
import {
  indianMobileSchema,
  NEW_STUDENT_STATUSES,
  type PortalStatus,
  STUDENT_STATUSES,
  type StudentDto,
  studentCoreFields,
} from "./students";

/**
 * The admission form engine: which details a school collects about a student and their
 * family, which documents it asks for, and how admission numbers look. Every school starts
 * from these catalogues and switches fields on or off, makes them required, adds its own, and
 * decides what parents see. The API and the forms build their checks from the same settings
 * (`detailsSchema`, `customValuesSchema`, `missingForAdmission`), so they can't disagree.
 * See docs/student-onboarding-and-access.md §7.
 */

// ---------------------------------------------------------------------------------------------
// Field catalogue
// ---------------------------------------------------------------------------------------------

export const FIELD_GROUPS = [
  "student",
  "address",
  "transport",
  "health",
  "previous",
  "family",
  "other",
] as const;
export type FieldGroup = (typeof FIELD_GROUPS)[number];

export const FIELD_GROUP_LABELS: Record<FieldGroup, string> = {
  student: "About the student",
  address: "Home address",
  transport: "Pickup and drop",
  health: "Health",
  previous: "Previous school",
  family: "Parents and guardians",
  other: "Other details",
};

export type FieldType =
  | "text"
  | "longText"
  | "number"
  | "date"
  | "yesNo"
  | "choice"
  | "multiChoice"
  | "phone"
  | "pin"
  | "state";

/**
 * How private a field is. `sensitive` (religion, caste, income) and `health` (medical
 * details) are stored encrypted and shown only to people whose role allows it.
 */
export type FieldSensitivity = "none" | "sensitive" | "health";

export interface Choice {
  value: string;
  label: string;
}

const choices = (entries: Record<string, string>): Choice[] =>
  Object.entries(entries).map(([value, label]) => ({ value, label }));

export const BLOOD_GROUPS = choices({
  "A+": "A+",
  "A-": "A−",
  "B+": "B+",
  "B-": "B−",
  "AB+": "AB+",
  "AB-": "AB−",
  "O+": "O+",
  "O-": "O−",
  unknown: "Not known",
});

export const RELIGIONS = choices({
  hindu: "Hindu",
  muslim: "Muslim",
  christian: "Christian",
  sikh: "Sikh",
  buddhist: "Buddhist",
  jain: "Jain",
  parsi: "Parsi",
  other: "Other",
  not_given: "Prefer not to say",
});

export const SOCIAL_CATEGORIES = choices({
  general: "General",
  obc: "OBC",
  sc: "SC",
  st: "ST",
  ews: "EWS",
  not_given: "Prefer not to say",
});

export const MOTHER_TONGUES = choices({
  hindi: "Hindi",
  english: "English",
  bengali: "Bengali",
  marathi: "Marathi",
  telugu: "Telugu",
  tamil: "Tamil",
  gujarati: "Gujarati",
  urdu: "Urdu",
  kannada: "Kannada",
  odia: "Odia",
  malayalam: "Malayalam",
  punjabi: "Punjabi",
  assamese: "Assamese",
  maithili: "Maithili",
  sanskrit: "Sanskrit",
  konkani: "Konkani",
  nepali: "Nepali",
  sindhi: "Sindhi",
  dogri: "Dogri",
  kashmiri: "Kashmiri",
  manipuri: "Manipuri",
  bodo: "Bodo",
  santali: "Santali",
  other: "Other",
});

export const TRANSPORT_MODES = choices({
  parent: "Parents drop and pick up",
  school_bus: "School bus",
  school_van: "School van",
  private_van: "Private van or auto",
  walk: "Walks",
  bicycle: "Bicycle",
});

export const PREVIOUS_BOARDS = choices({
  cbse: "CBSE",
  cisce: "ICSE / ISC",
  state: "State board",
  ib: "IB",
  cambridge: "Cambridge (IGCSE)",
  nios: "NIOS",
  other: "Other",
});

export const NATIONALITIES = choices({ indian: "Indian", other: "Other" });

export interface FieldDef {
  key: string;
  group: FieldGroup;
  label: string;
  hint?: string;
  type: FieldType;
  options?: readonly Choice[];
  /** recommended: on and optional; optional: off until the school turns it on. */
  tier: "recommended" | "optional";
  sensitivity: FieldSensitivity;
  maxLength?: number;
  min?: number;
  max?: number;
  /** Asked only when another field has this value (e.g. the country when not Indian). */
  when?: { field: string; equals: string | boolean };
  /** Parents' and guardians' fields are asked once per person. */
  perPerson?: boolean;
}

const STATE_CHOICES: Choice[] = INDIAN_STATES.map((state) => ({
  value: state.code,
  label: state.name,
}));

/** Every built-in field a school can switch on, in the order the form asks them. */
export const FIELD_CATALOGUE = [
  // About the student (name, date of birth, gender and class are always asked).
  {
    key: "placeOfBirth",
    group: "student",
    label: "Place of birth",
    type: "text",
    tier: "optional",
    sensitivity: "none",
    maxLength: 80,
  },
  {
    key: "nationality",
    group: "student",
    label: "Nationality",
    type: "choice",
    options: NATIONALITIES,
    tier: "recommended",
    sensitivity: "none",
  },
  {
    key: "nationalityCountry",
    group: "student",
    label: "Country",
    hint: "For a student who isn't Indian.",
    type: "text",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 60,
    when: { field: "nationality", equals: "other" },
  },
  {
    key: "motherTongue",
    group: "student",
    label: "Mother tongue",
    type: "choice",
    options: MOTHER_TONGUES,
    tier: "recommended",
    sensitivity: "none",
  },
  {
    key: "religion",
    group: "student",
    label: "Religion",
    type: "choice",
    options: RELIGIONS,
    tier: "recommended",
    sensitivity: "sensitive",
  },
  {
    key: "socialCategory",
    group: "student",
    label: "Category",
    hint: "As on the caste certificate, if any.",
    type: "choice",
    options: SOCIAL_CATEGORIES,
    tier: "recommended",
    sensitivity: "sensitive",
  },
  {
    key: "caste",
    group: "student",
    label: "Caste",
    type: "text",
    tier: "optional",
    sensitivity: "sensitive",
    maxLength: 60,
  },
  {
    key: "identificationMarks",
    group: "student",
    label: "Identification marks",
    hint: "Like a mole on the right cheek.",
    type: "text",
    tier: "optional",
    sensitivity: "none",
    maxLength: 160,
  },
  {
    key: "singleGirlChild",
    group: "student",
    label: "Single girl child",
    type: "yesNo",
    tier: "optional",
    sensitivity: "none",
  },
  {
    key: "house",
    group: "student",
    label: "House",
    type: "text",
    tier: "optional",
    sensitivity: "none",
    maxLength: 40,
  },
  // Home address.
  {
    key: "addressLine1",
    group: "address",
    label: "House and street",
    type: "text",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 120,
  },
  {
    key: "addressLine2",
    group: "address",
    label: "Area or landmark",
    type: "text",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 120,
  },
  {
    key: "city",
    group: "address",
    label: "City, town or village",
    type: "text",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 80,
  },
  {
    key: "district",
    group: "address",
    label: "District",
    type: "text",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 80,
  },
  {
    key: "stateCode",
    group: "address",
    label: "State",
    type: "state",
    options: STATE_CHOICES,
    tier: "recommended",
    sensitivity: "none",
  },
  {
    key: "pin",
    group: "address",
    label: "PIN code",
    type: "pin",
    tier: "recommended",
    sensitivity: "none",
  },
  {
    key: "permanentAddress",
    group: "address",
    label: "Permanent address",
    hint: "Only if it's different from the home address.",
    type: "longText",
    tier: "optional",
    sensitivity: "none",
    maxLength: 300,
  },
  // Pickup and drop.
  {
    key: "transportMode",
    group: "transport",
    label: "How the student comes to school",
    type: "choice",
    options: TRANSPORT_MODES,
    tier: "recommended",
    sensitivity: "none",
  },
  {
    key: "pickupSameAsHome",
    group: "transport",
    label: "Picked up from home",
    type: "yesNo",
    tier: "recommended",
    sensitivity: "none",
  },
  {
    key: "pickupAddress",
    group: "transport",
    label: "Pickup address",
    type: "longText",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 300,
    when: { field: "pickupSameAsHome", equals: false },
  },
  {
    key: "dropSameAsHome",
    group: "transport",
    label: "Dropped at home",
    type: "yesNo",
    tier: "recommended",
    sensitivity: "none",
  },
  {
    key: "dropAddress",
    group: "transport",
    label: "Drop address",
    type: "longText",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 300,
    when: { field: "dropSameAsHome", equals: false },
  },
  {
    key: "transportNote",
    group: "transport",
    label: "Route or stop",
    hint: "Bus number, stop name or anything the driver should know.",
    type: "text",
    tier: "optional",
    sensitivity: "none",
    maxLength: 160,
  },
  // Health.
  {
    key: "bloodGroup",
    group: "health",
    label: "Blood group",
    type: "choice",
    options: BLOOD_GROUPS,
    tier: "recommended",
    sensitivity: "none",
  },
  {
    key: "medicalConditions",
    group: "health",
    label: "Health conditions",
    hint: 'Like asthma, epilepsy or diabetes. Write "None" if there are none.',
    type: "longText",
    tier: "recommended",
    sensitivity: "health",
    maxLength: 500,
  },
  {
    key: "allergies",
    group: "health",
    label: "Allergies",
    hint: 'Food, medicine or anything else. Write "None" if there are none.',
    type: "longText",
    tier: "recommended",
    sensitivity: "health",
    maxLength: 500,
  },
  {
    key: "medication",
    group: "health",
    label: "Regular medicines",
    type: "longText",
    tier: "optional",
    sensitivity: "health",
    maxLength: 500,
  },
  {
    key: "specialNeeds",
    group: "health",
    label: "Special needs or disability",
    hint: "Anything the school should plan for.",
    type: "longText",
    tier: "optional",
    sensitivity: "health",
    maxLength: 500,
  },
  {
    key: "doctorName",
    group: "health",
    label: "Family doctor",
    type: "text",
    tier: "optional",
    sensitivity: "health",
    maxLength: 80,
  },
  {
    key: "doctorPhone",
    group: "health",
    label: "Doctor's phone",
    type: "phone",
    tier: "optional",
    sensitivity: "health",
  },
  {
    key: "heightCm",
    group: "health",
    label: "Height (cm)",
    type: "number",
    tier: "optional",
    sensitivity: "none",
    min: 40,
    max: 220,
  },
  {
    key: "weightKg",
    group: "health",
    label: "Weight (kg)",
    type: "number",
    tier: "optional",
    sensitivity: "none",
    min: 5,
    max: 200,
  },
  // Previous school.
  {
    key: "prevSchoolName",
    group: "previous",
    label: "Previous school",
    type: "text",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 120,
  },
  {
    key: "prevSchoolBoard",
    group: "previous",
    label: "Board",
    type: "choice",
    options: PREVIOUS_BOARDS,
    tier: "recommended",
    sensitivity: "none",
  },
  {
    key: "prevSchoolCity",
    group: "previous",
    label: "City",
    type: "text",
    tier: "optional",
    sensitivity: "none",
    maxLength: 80,
  },
  {
    key: "prevClass",
    group: "previous",
    label: "Last class studied",
    type: "text",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 20,
  },
  {
    key: "prevYear",
    group: "previous",
    label: "Year",
    hint: "Like 2025-26.",
    type: "text",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 9,
  },
  {
    key: "prevResult",
    group: "previous",
    label: "Result or percentage",
    type: "text",
    tier: "optional",
    sensitivity: "none",
    maxLength: 40,
  },
  {
    key: "tcNumber",
    group: "previous",
    label: "Transfer certificate number",
    type: "text",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 40,
  },
  {
    key: "tcDate",
    group: "previous",
    label: "Transfer certificate date",
    type: "date",
    tier: "recommended",
    sensitivity: "none",
  },
  {
    key: "leavingReason",
    group: "previous",
    label: "Reason for leaving",
    type: "text",
    tier: "optional",
    sensitivity: "none",
    maxLength: 160,
  },
  // Asked for each parent or guardian (name, relation and mobile are always asked).
  {
    key: "email",
    group: "family",
    label: "Email",
    type: "text",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 120,
    perPerson: true,
  },
  {
    key: "occupation",
    group: "family",
    label: "Occupation",
    type: "text",
    tier: "recommended",
    sensitivity: "none",
    maxLength: 80,
    perPerson: true,
  },
  {
    key: "qualification",
    group: "family",
    label: "Education",
    hint: "Highest qualification, like B.Com.",
    type: "text",
    tier: "optional",
    sensitivity: "none",
    maxLength: 80,
    perPerson: true,
  },
  {
    key: "incomeBand",
    group: "family",
    label: "Annual income",
    type: "choice",
    options: choices({
      below_1l: "Below ₹1 lakh",
      "1l_3l": "₹1–3 lakh",
      "3l_5l": "₹3–5 lakh",
      "5l_10l": "₹5–10 lakh",
      above_10l: "Above ₹10 lakh",
      not_given: "Prefer not to say",
    }),
    tier: "optional",
    sensitivity: "sensitive",
    perPerson: true,
  },
  {
    key: "altMobile",
    group: "family",
    label: "Other mobile",
    type: "phone",
    tier: "optional",
    sensitivity: "none",
    perPerson: true,
  },
  {
    key: "officeAddress",
    group: "family",
    label: "Office address",
    type: "longText",
    tier: "optional",
    sensitivity: "none",
    maxLength: 300,
    perPerson: true,
  },
] as const satisfies readonly FieldDef[];

export type DetailKey = Exclude<(typeof FIELD_CATALOGUE)[number], { perPerson: true }>["key"];
export type PersonFieldKey = Extract<(typeof FIELD_CATALOGUE)[number], { perPerson: true }>["key"];

export const DETAIL_FIELDS: readonly FieldDef[] = FIELD_CATALOGUE.filter(
  (field: FieldDef) => !field.perPerson,
);
export const PERSON_FIELDS: readonly FieldDef[] = FIELD_CATALOGUE.filter(
  (field: FieldDef) => field.perPerson === true,
);

export function catalogueField(key: string): FieldDef | undefined {
  return (FIELD_CATALOGUE as readonly FieldDef[]).find((field) => field.key === key);
}

// ---------------------------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------------------------

/**
 * How much a school needs a document. Nothing ever blocks admission up to Class 8 (RTE ss. 5
 * and 14): a missing "required" document shows as pending until it's handed in.
 */
export const DOCUMENT_REQUIREMENTS = ["required", "if_applicable", "optional", "off"] as const;
export type DocumentRequirement = (typeof DOCUMENT_REQUIREMENTS)[number];

export const DOCUMENT_REQUIREMENT_LABELS: Record<DocumentRequirement, string> = {
  required: "Required",
  if_applicable: "If it applies",
  optional: "Optional",
  off: "Not asked",
};

export const STUDENT_DOCUMENT_CATALOGUE = [
  { kind: "birth_certificate", label: "Birth certificate", requirement: "required" },
  {
    kind: "aadhaar_card",
    label: "Aadhaar card",
    requirement: "optional",
    hint: "Never required for admission.",
    sensitivity: "sensitive",
  },
  { kind: "father_id_proof", label: "Father's ID proof", requirement: "optional" },
  { kind: "mother_id_proof", label: "Mother's ID proof", requirement: "optional" },
  { kind: "address_proof", label: "Address proof", requirement: "optional" },
  { kind: "transfer_certificate", label: "Transfer certificate", requirement: "if_applicable" },
  {
    kind: "previous_marksheet",
    label: "Previous marksheet or report card",
    requirement: "if_applicable",
  },
  {
    kind: "caste_certificate",
    label: "Caste certificate",
    requirement: "if_applicable",
    sensitivity: "sensitive",
  },
  {
    kind: "medical_certificate",
    label: "Medical certificate",
    requirement: "if_applicable",
    sensitivity: "health",
  },
] as const satisfies readonly {
  kind: string;
  label: string;
  requirement: DocumentRequirement;
  hint?: string;
  /** Opened and listed only for people allowed private (or health) details. */
  sensitivity?: Exclude<FieldSensitivity, "none">;
}[];

/** How private a kind of document is; the school's own kinds are ordinary. */
export function documentSensitivity(kind: string): FieldSensitivity {
  const found = STUDENT_DOCUMENT_CATALOGUE.find((doc) => doc.kind === kind);
  return found && "sensitivity" in found ? found.sensitivity : "none";
}

export type BuiltInDocumentKind = (typeof STUDENT_DOCUMENT_CATALOGUE)[number]["kind"];

export const STUDENT_DOCUMENT_STATUSES = ["pending", "verified", "rejected"] as const;
export type StudentDocumentStatus = (typeof STUDENT_DOCUMENT_STATUSES)[number];

export const STUDENT_DOCUMENT_STATUS_LABELS: Record<StudentDocumentStatus, string> = {
  pending: "Waiting for check",
  verified: "Verified",
  rejected: "Rejected",
};

/** Files a student document or photo may be: PDF, JPEG or PNG, up to 5 MB. */
export const STUDENT_FILE_MAX_BYTES = 5 * 1024 * 1024;
export const STUDENT_FILE_TYPES = ["application/pdf", "image/jpeg", "image/png"] as const;
export const STUDENT_PHOTO_TYPES = ["image/jpeg", "image/png"] as const;
export const STUDENT_PHOTO_MAX_BYTES = 2 * 1024 * 1024;

// ---------------------------------------------------------------------------------------------
// People: parents, guardians, emergency contacts and who may pick the student up
// ---------------------------------------------------------------------------------------------

export const PERSON_RELATIONS = [
  "father",
  "mother",
  "guardian",
  "grandparent",
  "uncle_aunt",
  "sibling",
  "driver",
  "other",
] as const;
export type PersonRelation = (typeof PERSON_RELATIONS)[number];

export const PERSON_RELATION_LABELS: Record<PersonRelation, string> = {
  father: "Father",
  mother: "Mother",
  guardian: "Guardian",
  grandparent: "Grandparent",
  uncle_aunt: "Uncle or aunt",
  sibling: "Elder brother or sister",
  driver: "Driver",
  other: "Other",
};

const personIdSchema = z.string().regex(/^gdn_[0-9A-Z]{26}$/, { error: "Unknown person" });

const optionalText = (max: number, tooLong: string) =>
  z
    .string()
    .trim()
    .max(max, { error: tooLong })
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional();

/**
 * A person linked to the student. A new person needs a mobile; for someone already saved, a
 * blank mobile keeps the saved one (it's never sent back to the browser in full).
 */
const personFields = z.object({
  id: personIdSchema.optional(),
  name: z
    .string()
    .trim()
    .min(1, { error: "Enter their name" })
    .max(80, { error: "Name is too long" }),
  relation: z.enum(PERSON_RELATIONS, { error: "Choose how they're related" }),
  mobile: z.preprocess(
    (value) => (value === "" ? undefined : value),
    indianMobileSchema.optional(),
  ),
  altMobile: z.preprocess(
    (value) => (value === "" ? null : value),
    indianMobileSchema.nullable().optional(),
  ),
  email: z.preprocess((value) => (value === "" ? null : value), emailSchema.nullable().optional()),
  occupation: optionalText(80, "Occupation is too long"),
  qualification: optionalText(80, "Education is too long"),
  incomeBand: z.preprocess(
    (value) => (value === "" ? null : value),
    z
      .enum(["below_1l", "1l_3l", "3l_5l", "5l_10l", "above_10l", "not_given"], {
        error: "Choose an income band",
      })
      .nullable()
      .optional(),
  ),
  officeAddress: optionalText(300, "Office address is too long"),
  /** Gets the parent login and messages. Exactly one person is primary. */
  isPrimary: z.boolean().default(false),
  livesWith: z.boolean().default(true),
  isEmergency: z.boolean().default(false),
  canPickUp: z.boolean().default(false),
});

export const personSchema = personFields.superRefine((person, ctx) => {
  if (!person.id && !person.mobile) {
    ctx.addIssue({ code: "custom", path: ["mobile"], message: "Enter a 10-digit mobile number" });
  }
});

export type PersonInput = z.input<typeof personSchema>;
export type Person = z.output<typeof personSchema>;

/** At least one person, exactly one of them the primary contact. */
export const peopleSchema = z
  .array(personSchema)
  .min(1, { error: "Add at least one parent or guardian" })
  .max(10, { error: "At most 10 people" })
  .superRefine((people, ctx) => {
    const ids = people.flatMap((person) => (person.id ? [person.id] : []));
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({ code: "custom", message: "The same person is listed twice" });
    }
    const primary = people.filter((person) => person.isPrimary).length;
    if (primary !== 1) {
      ctx.addIssue({
        code: "custom",
        message: "Choose one person as the main contact",
      });
    }
  });

// ---------------------------------------------------------------------------------------------
// School settings
// ---------------------------------------------------------------------------------------------

/** What parents see of a field in their portal: nothing, the value, or the value to change. */
export const PARENT_ACCESS = ["hidden", "view", "edit"] as const;
export type ParentAccess = (typeof PARENT_ACCESS)[number];

export interface FieldSetting {
  enabled: boolean;
  required: boolean;
  parent: ParentAccess;
  publicForm: boolean;
}

const fieldSettingSchema = z.object({
  enabled: z.boolean(),
  required: z.boolean(),
  parent: z.enum(PARENT_ACCESS),
  publicForm: z.boolean(),
});

export const CUSTOM_FIELD_TYPES = [
  "text",
  "longText",
  "number",
  "date",
  "yesNo",
  "choice",
  "multiChoice",
] as const satisfies readonly FieldType[];
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];

export const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: "Short text",
  longText: "Long text",
  number: "Number",
  date: "Date",
  yesNo: "Yes or no",
  choice: "One choice from a list",
  multiChoice: "Several choices from a list",
};

export const customFieldSchema = z
  .object({
    id: z.string().regex(/^cf_[a-z0-9]{6,32}$/, { error: "Unknown field" }),
    label: z
      .string()
      .trim()
      .min(1, { error: "Enter the question" })
      .max(60, { error: "Question is too long" }),
    hint: optionalText(160, "Help text is too long"),
    type: z.enum(CUSTOM_FIELD_TYPES, { error: "Choose the kind of answer" }),
    options: z
      .array(
        z
          .string()
          .trim()
          .min(1, { error: "Enter the choice" })
          .max(40, { error: "Choice is too long" }),
      )
      .max(30, { error: "At most 30 choices" })
      .default([]),
    group: z.enum(FIELD_GROUPS).exclude(["family"]).default("other"),
    required: z.boolean().default(false),
    sensitive: z.boolean().default(false),
    parent: z.enum(PARENT_ACCESS).default("view"),
    publicForm: z.boolean().default(false),
    /** A field that may hold answers is retired, never deleted. */
    status: z.enum(["active", "retired"]).default("active"),
  })
  .superRefine((field, ctx) => {
    if ((field.type === "choice" || field.type === "multiChoice") && field.options.length < 2) {
      ctx.addIssue({ code: "custom", path: ["options"], message: "Give at least 2 choices" });
    }
    if (
      new Set(field.options.map((option) => option.toLowerCase())).size !== field.options.length
    ) {
      ctx.addIssue({ code: "custom", path: ["options"], message: "A choice is listed twice" });
    }
  });

export type CustomField = z.output<typeof customFieldSchema>;
export type CustomFieldInput = z.input<typeof customFieldSchema>;

export const customDocumentSchema = z.object({
  id: z.string().regex(/^cd_[a-z0-9]{6,32}$/, { error: "Unknown document" }),
  label: z
    .string()
    .trim()
    .min(1, { error: "Enter the document's name" })
    .max(60, { error: "Name is too long" }),
  requirement: z.enum(DOCUMENT_REQUIREMENTS).exclude(["off"]),
  status: z.enum(["active", "retired"]).default("active"),
});

export type CustomDocument = z.output<typeof customDocumentSchema>;

/** `{YYYY}`, `{YY}` and `{SEQ}` / `{SEQ:4}` with letters, digits, / and - around them. */
export const admissionNumberFormatSchema = z
  .string()
  .trim()
  .toUpperCase()
  .min(1, { error: "Enter a format, like {YYYY}/{SEQ:4}" })
  .max(40, { error: "Format is too long" })
  .refine((format) => (format.match(/\{SEQ(:[1-8])?\}/g) ?? []).length === 1, {
    error: "Put {SEQ} (or {SEQ:4} for 0001) in the format once",
  })
  .refine((format) => /^[A-Z0-9/-]*$/.test(format.replace(/\{(YYYY|YY|SEQ(:[1-8])?)\}/g, "")), {
    error: "Use letters, numbers, / or - around {YYYY}, {YY} and {SEQ}",
  });

const settingsBody = {
  /** Changes from the catalogue's defaults, by field key. */
  fields: z.record(z.string(), fieldSettingSchema.partial()).default({}),
  customFields: z
    .array(customFieldSchema)
    .max(60, { error: "At most 60 of your own questions" })
    .default([]),
  /** Requirement per built-in document kind, where it differs from the catalogue. */
  documents: z.record(z.string(), z.enum(DOCUMENT_REQUIREMENTS)).default({}),
  customDocuments: z
    .array(customDocumentSchema)
    .max(30, { error: "At most 30 of your own documents" })
    .default([]),
  photo: z.enum(["required", "optional"]).default("optional"),
  family: z
    .object({ fatherRequired: z.boolean(), motherRequired: z.boolean() })
    .default({ fatherRequired: false, motherRequired: false }),
  admissionNumber: z
    .object({ format: admissionNumberFormatSchema })
    .default({ format: "{YYYY}/{SEQ:4}" }),
  /** Students in this class and the ones after it get a sign-in of their own; null: none do. */
  portal: z
    .object({ studentLoginFromClassId: z.string().trim().min(1).max(64).nullable() })
    .default({ studentLoginFromClassId: null }),
  /** The public admission page (`/apply/<Institution ID>`): off until the school turns it on. */
  online: z
    .object({
      enabled: z.boolean(),
      /** A full application, or a short enquiry (the child and a contact). */
      mode: z.enum(["application", "enquiry"]),
      /** Classes families may apply for. */
      classIds: z.array(z.string().trim().min(1).max(64)).max(50, { error: "At most 50 classes" }),
      /** A few lines on the page: dates, fees, what happens next. */
      intro: z
        .string()
        .trim()
        .max(600, { error: "Keep it under 600 characters" })
        .transform((value) => (value === "" ? null : value))
        .nullable(),
    })
    .default({ enabled: false, mode: "application", classIds: [], intro: null }),
};

export const admissionSettingsSchema = z.object(settingsBody).superRefine((settings, ctx) => {
  for (const key of Object.keys(settings.fields)) {
    if (!catalogueField(key)) {
      ctx.addIssue({ code: "custom", path: ["fields", key], message: "Unknown field" });
    }
  }
  for (const kind of Object.keys(settings.documents)) {
    if (!STUDENT_DOCUMENT_CATALOGUE.some((doc) => doc.kind === kind)) {
      ctx.addIssue({ code: "custom", path: ["documents", kind], message: "Unknown document" });
    }
  }
  const ids = settings.customFields.map((field) => field.id);
  if (new Set(ids).size !== ids.length) {
    ctx.addIssue({ code: "custom", path: ["customFields"], message: "A question is listed twice" });
  }
  const labels = settings.customFields.map((field) => field.label.toLowerCase());
  if (new Set(labels).size !== labels.length) {
    ctx.addIssue({
      code: "custom",
      path: ["customFields"],
      message: "Two of your questions have the same wording",
    });
  }
  if (settings.online.enabled && settings.online.classIds.length === 0) {
    ctx.addIssue({
      code: "custom",
      path: ["online", "classIds"],
      message: "Choose the classes families may apply for",
    });
  }
});

export type AdmissionSettings = z.output<typeof admissionSettingsSchema>;
export type AdmissionSettingsInput = z.input<typeof admissionSettingsSchema>;

/** A school that hasn't changed anything yet. */
export const DEFAULT_ADMISSION_SETTINGS: AdmissionSettings = admissionSettingsSchema.parse({});

export const admissionSettingsUpdateSchema = z.object({
  settings: admissionSettingsSchema,
  version: z.number().int().min(0),
});

export interface AdmissionSettingsDto {
  settings: AdmissionSettings;
  /** 0 until the school saves its first change. */
  version: number;
  updatedAt: string | null;
}

/** The catalogue's default for a field: recommended ones on, optional ones off. */
export function defaultFieldSetting(field: FieldDef): FieldSetting {
  return {
    enabled: field.tier === "recommended",
    required: false,
    parent: field.sensitivity === "none" ? "edit" : "view",
    publicForm: field.tier === "recommended" && field.group !== "previous",
  };
}

/** A field's setting for this school: the catalogue's default with the school's changes. */
export function fieldSetting(settings: AdmissionSettings, field: FieldDef): FieldSetting {
  const setting = { ...defaultFieldSetting(field), ...settings.fields[field.key] };
  // A switched-off field is never required, and a dependent field follows its parent.
  if (!setting.enabled) return { ...setting, required: false, publicForm: false };
  if (field.when) {
    const parent = catalogueField(field.when.field);
    if (parent && !fieldSetting(settings, parent).enabled) {
      return { ...setting, enabled: false, required: false, publicForm: false };
    }
  }
  return setting;
}

export function documentRequirement(
  settings: AdmissionSettings,
  kind: string,
): DocumentRequirement {
  const builtIn = STUDENT_DOCUMENT_CATALOGUE.find((doc) => doc.kind === kind);
  if (builtIn) return settings.documents[kind] ?? builtIn.requirement;
  const custom = settings.customDocuments.find((doc) => doc.id === kind);
  return custom && custom.status === "active" ? custom.requirement : "off";
}

/** Documents the school asks for, built-in then its own, with their requirement. */
export function askedDocuments(
  settings: AdmissionSettings,
): { kind: string; label: string; requirement: Exclude<DocumentRequirement, "off"> }[] {
  return [
    ...STUDENT_DOCUMENT_CATALOGUE.map((doc) => ({ kind: doc.kind as string, label: doc.label })),
    ...settings.customDocuments
      .filter((doc) => doc.status === "active")
      .map((doc) => ({ kind: doc.id, label: doc.label })),
  ].flatMap((doc) => {
    const requirement = documentRequirement(settings, doc.kind);
    return requirement === "off" ? [] : [{ ...doc, requirement }];
  });
}

// ---------------------------------------------------------------------------------------------
// Checking values with a school's settings
// ---------------------------------------------------------------------------------------------

export type DetailValue = string | number | boolean | string[] | null;
export type DetailValues = Record<string, DetailValue>;

export const FORM_AUDIENCES = ["office", "portal", "public"] as const;
export type FormAudience = (typeof FORM_AUDIENCES)[number];

/** Whether a field is asked of this audience at all. */
export function askedOf(setting: FieldSetting, audience: FormAudience): boolean {
  if (!setting.enabled) return false;
  if (audience === "portal") return setting.parent !== "hidden";
  if (audience === "public") return setting.publicForm;
  return true;
}

const blankToNull = (value: unknown) =>
  value === "" || value === undefined
    ? null
    : typeof value === "string"
      ? value.trim() || null
      : value;

/** The check for one value of a given type; blanks become null. */
export function valueSchema(field: {
  label: string;
  type: FieldType;
  options?: readonly (Choice | string)[];
  maxLength?: number;
  min?: number;
  max?: number;
}): z.ZodType<DetailValue> {
  const values = (field.options ?? []).map((option) =>
    typeof option === "string" ? option : option.value,
  );
  const inner: z.ZodType = (() => {
    switch (field.type) {
      case "text":
      case "longText":
        return z.string().max(field.maxLength ?? (field.type === "text" ? 120 : 1000), {
          error: `${field.label} is too long`,
        });
      case "number":
        return z.coerce
          .number({ error: "Enter a number" })
          .min(field.min ?? -1_000_000, { error: `At least ${field.min ?? -1_000_000}` })
          .max(field.max ?? 1_000_000, { error: `At most ${field.max ?? 1_000_000}` });
      case "date":
        return z.iso.date({ error: "Enter the date as YYYY-MM-DD" });
      case "yesNo":
        return z.boolean({ error: "Choose yes or no" });
      case "choice":
        return z.string().refine((value) => values.includes(value), {
          error: `Choose ${field.label.toLowerCase()} from the list`,
        });
      case "multiChoice":
        return z
          .array(z.string())
          .refine((picked) => picked.every((value) => values.includes(value)), {
            error: "Choose from the list",
          });
      case "phone":
        return indianMobileSchema;
      case "pin":
        return z.string().regex(/^[1-9]\d{5}$/, { error: "Enter the 6-digit PIN code" });
      case "state":
        return stateCodeSchema;
    }
  })();
  return z.preprocess(blankToNull, inner.nullable()) as z.ZodType<DetailValue>;
}

/**
 * The check for a student's built-in details, for this school and audience. Fields not asked
 * of the audience are dropped; every asked field may be left blank here (completeness is
 * `missingForAdmission`'s job, so a draft can be saved).
 */
export function detailsSchema(settings: AdmissionSettings, audience: FormAudience) {
  const shape: Record<string, z.ZodType> = {};
  for (const field of DETAIL_FIELDS) {
    if (askedOf(fieldSetting(settings, field), audience)) {
      shape[field.key] = valueSchema(field).optional();
    }
  }
  return z.object(shape);
}

/** The school's own questions that are in use and asked of this audience. */
export function askedCustomFields(
  settings: AdmissionSettings,
  audience: FormAudience,
): CustomField[] {
  return settings.customFields.filter(
    (field) =>
      field.status === "active" &&
      (audience === "office" ||
        (audience === "portal" ? field.parent !== "hidden" : field.publicForm)),
  );
}

export function customValuesSchema(settings: AdmissionSettings, audience: FormAudience) {
  const shape: Record<string, z.ZodType> = {};
  for (const field of askedCustomFields(settings, audience)) {
    shape[field.id] = valueSchema({
      ...field,
      maxLength: field.type === "text" ? 160 : 1000,
    }).optional();
  }
  return z.object(shape);
}

const isBlank = (value: DetailValue | undefined) =>
  value === null ||
  value === undefined ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/** A dependent field is asked only when its parent field has the value it depends on. */
export function fieldApplies(field: FieldDef, values: DetailValues): boolean {
  if (!field.when) return true;
  return values[field.when.field] === field.when.equals;
}

export interface MissingItem {
  /** "details.pin", "custom.cf_x", "people", "photo", "documents.birth_certificate" */
  path: string;
  label: string;
  /** Documents and the photo never block admission; they only show as "needs completing". */
  blocking: boolean;
}

/**
 * What a record still lacks for this school: required details and questions, the family the
 * school asks for, the photo and required documents.
 */
export function missingForAdmission(
  settings: AdmissionSettings,
  record: {
    details: DetailValues;
    custom: DetailValues;
    people: { relation: PersonRelation }[];
    hasPhoto: boolean;
    documentKinds: readonly string[];
  },
  audience: FormAudience = "office",
): MissingItem[] {
  const missing: MissingItem[] = [];
  for (const field of DETAIL_FIELDS) {
    const setting = fieldSetting(settings, field);
    if (
      setting.required &&
      askedOf(setting, audience) &&
      fieldApplies(field, record.details) &&
      isBlank(record.details[field.key])
    ) {
      missing.push({ path: `details.${field.key}`, label: field.label, blocking: true });
    }
  }
  for (const field of askedCustomFields(settings, audience)) {
    if (field.required && isBlank(record.custom[field.id])) {
      missing.push({ path: `custom.${field.id}`, label: field.label, blocking: true });
    }
  }
  for (const [relation, required] of [
    ["father", settings.family.fatherRequired],
    ["mother", settings.family.motherRequired],
  ] as const) {
    if (required && !record.people.some((person) => person.relation === relation)) {
      missing.push({
        path: "people",
        label: `${PERSON_RELATION_LABELS[relation]}'s details`,
        blocking: true,
      });
    }
  }
  if (settings.photo === "required" && !record.hasPhoto) {
    missing.push({ path: "photo", label: "Student's photo", blocking: false });
  }
  for (const doc of askedDocuments(settings)) {
    if (doc.requirement === "required" && !record.documentKinds.includes(doc.kind)) {
      missing.push({ path: `documents.${doc.kind}`, label: doc.label, blocking: false });
    }
  }
  return missing;
}

// ---------------------------------------------------------------------------------------------
// Admission numbers and duplicates
// ---------------------------------------------------------------------------------------------

/** "{YYYY}/{SEQ:4}" in 2026, number 7 → "2026/0007". */
export function formatAdmissionNumber(format: string, year: number, n: number): string {
  return format
    .replaceAll("{YYYY}", String(year))
    .replaceAll("{YY}", String(year).slice(-2))
    .replace(/\{SEQ(?::([1-8]))?\}/, (_, width?: string) =>
      String(n).padStart(Number(width ?? 1), "0"),
    );
}

/**
 * The counter a format draws from: one per year when the year is in the number (so it starts
 * again at 1 each year), otherwise one for good.
 */
export function admissionSeriesKey(format: string, year: number): string {
  return /\{YY(YY)?\}/.test(format) ? `admission:${year}` : "admission";
}

/** "Aarav  Sharma" → "aarav sharma": what duplicate checks compare. */
export function normalisedName(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function labelOf(options: readonly Choice[] | undefined, value: DetailValue): string {
  if (value === null) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return options?.find((option) => option.value === value)?.label ?? String(value);
}

// ---------------------------------------------------------------------------------------------
// The student record: what the office sends, and what it gets back
// ---------------------------------------------------------------------------------------------

/**
 * An older form sends one `guardianPhone`; it becomes the main contact, so a page loaded
 * before an update keeps working.
 */
function withLegacyPhone(value: unknown): unknown {
  if (value === null || typeof value !== "object") return value;
  const input = value as Record<string, unknown>;
  if (input.people !== undefined || typeof input.guardianPhone !== "string") return value;
  const { guardianPhone, ...rest } = input;
  return {
    ...rest,
    people: [
      {
        name: "Parent or guardian",
        relation: "guardian",
        mobile: guardianPhone,
        isPrimary: true,
        livesWith: true,
        isEmergency: true,
        canPickUp: true,
      },
    ],
  };
}

const valuesRecord = z.record(z.string(), z.unknown()).default({});

/**
 * A new student. A blank admission number takes the school's next one. Details and the
 * school's own questions are checked against its settings by the API (`detailsSchema`).
 */
const studentCreateBody = studentCoreFields.extend({
  admissionNo: z.preprocess(
    (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
    studentCoreFields.shape.admissionNo.optional(),
  ),
  lastName: studentCoreFields.shape.lastName.default(""),
  admissionDate: z.iso.date({ error: "Enter the admission date as YYYY-MM-DD" }).optional(),
  status: z.enum(NEW_STUDENT_STATUSES, { error: "Choose a status" }).default("active"),
  details: valuesRecord,
  custom: valuesRecord,
  people: peopleSchema,
});

export const studentCreateSchema = z.preprocess(withLegacyPhone, studentCreateBody);

/**
 * A change to a student. Only what's sent changes: details and answers by key (null clears
 * one). `guardianPhone` changes the main contact's mobile, for older forms.
 */
export const studentUpdateSchema = studentCoreFields
  .partial()
  .extend({
    admissionDate: z.iso.date({ error: "Enter the admission date as YYYY-MM-DD" }).optional(),
    status: z.enum(STUDENT_STATUSES, { error: "Choose a status" }).optional(),
    details: z.record(z.string(), z.unknown()).optional(),
    custom: z.record(z.string(), z.unknown()).optional(),
    guardianPhone: indianMobileSchema.optional(),
    /** Optimistic concurrency: the version the editor started from. */
    version: z.number().int().positive(),
  })
  .refine(
    (value) =>
      Object.entries(value).some(
        ([key, fieldValue]) => key !== "version" && fieldValue !== undefined,
      ),
    { error: "Change at least one field" },
  );

export type StudentCreateInput = z.input<typeof studentCreateBody>;
export type StudentCreate = z.output<typeof studentCreateBody>;
export type StudentUpdate = z.output<typeof studentUpdateSchema>;

/** A change to one of a student's people: a blank mobile keeps the saved one. */
export const personSaveSchema = z.object({
  person: personFields.omit({ id: true }),
  /** The link's version, so a change made meanwhile isn't overwritten. */
  version: z.number().int().positive(),
  /** The person's own version: they're shared, so a sibling's page may have changed them. */
  personVersion: z.number().int().positive(),
});

export const documentReviewInputSchema = z
  .object({
    decision: z.enum(["verified", "rejected"], { error: "Choose verified or rejected" }),
    note: optionalText(300, "Note is too long"),
    version: z.number().int().positive(),
  })
  .superRefine((review, ctx) => {
    if (review.decision === "rejected" && !review.note) {
      ctx.addIssue({
        code: "custom",
        path: ["note"],
        message: "Say what's wrong, so the family can fix it",
      });
    }
  });

/** Possible duplicates, checked before adding a student. */
export const duplicateCheckSchema = z.object({
  firstName: z.string().trim().max(80).default(""),
  lastName: z.string().trim().max(80).default(""),
  dateOfBirth: z.iso.date().optional(),
  mobile: z.preprocess(
    (value) => (value === "" ? undefined : value),
    indianMobileSchema.optional(),
  ),
});

export interface PersonDto {
  id: string;
  name: string;
  relation: PersonRelation;
  /** Masked "••••••3210" unless the caller may see contact details. */
  mobile: string;
  mobileMasked: boolean;
  altMobile: string | null;
  email: string | null;
  occupation: string | null;
  qualification: string | null;
  /** Null when hidden from the caller. */
  incomeBand: string | null;
  officeAddress: string | null;
  isPrimary: boolean;
  livesWith: boolean;
  isEmergency: boolean;
  canPickUp: boolean;
  /** When the photo last changed; null without one. */
  photoUpdatedAt: string | null;
  /** Other students this person is linked to (siblings), by name and class. */
  otherStudents: { id: string; name: string; sectionName: string }[];
  /** The link's version, for changes. */
  version: number;
  /** The person's own version (shared by all their children), for changes. */
  personVersion: number;
}

export interface StudentDocumentDto {
  id: string;
  kind: string;
  label: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  status: StudentDocumentStatus;
  reviewNote: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  uploadedVia: "office" | "portal" | "public";
  uploadedBy: string | null;
  uploadedAt: string;
  version: number;
}

export interface StudentProfileDto extends StudentDto {
  /** Built-in details the school asks; sensitive and health ones are absent when hidden. */
  details: DetailValues;
  custom: DetailValues;
  people: PersonDto[];
  documents: StudentDocumentDto[];
  /** What the record still lacks for this school. */
  missing: MissingItem[];
  /** Which kinds of details were left out because the caller may not see them. */
  hidden: { sensitive: boolean; health: boolean };
  /** The family side: status, sign-ins, changes waiting. */
  portal: StudentPortalDto;
  can: StudentDto["can"] & {
    documents: boolean;
    verify: boolean;
    /** Invite the family and approve what they send. */
    managePortal: boolean;
    /** A family member asking for changes (they never change the record directly). */
    requestChange: boolean;
    /** Staff seeing the school's own record; false for families (they see what parents may). */
    internal: boolean;
  };
}

export interface DuplicateMatchDto {
  id: string;
  name: string;
  admissionNo: string;
  sectionName: string;
  dateOfBirth: string;
  /** Why it matched: same name and date of birth, or the same parent's mobile. */
  reason: "name_and_birth" | "parent_mobile";
}

export interface SiblingContactDto {
  /** The person already saved, to link instead of typing again. */
  personId: string;
  name: string;
  relation: PersonRelation;
  students: { id: string; name: string; sectionName: string }[];
}

export interface DuplicateCheckDto {
  matches: DuplicateMatchDto[];
  siblings: SiblingContactDto[];
}

export interface StudentHistoryEntry {
  at: string;
  actor: string | null;
  action: string;
  /** What changed, in words: "Changed blood group, allergies". Never the values. */
  summary: string;
}

// ---------------------------------------------------------------------------------------------
// Family portal: invites, the first check of a record, and changes asked for later
// ---------------------------------------------------------------------------------------------

/** Who on a student's record may get a parent sign-in (never a driver or other contact). */
export const PORTAL_RELATIONS: readonly PersonRelation[] = [
  "father",
  "mother",
  "guardian",
  "grandparent",
  "uncle_aunt",
];

/** The admission form as families may see it: without questions the school keeps to itself. */
export function settingsForParents(settings: AdmissionSettings): AdmissionSettings {
  return {
    ...settings,
    customFields: settings.customFields.filter((field) => field.parent !== "hidden"),
  };
}

/** Statuses in which the family is still completing the record and the app stays closed. */
export const PORTAL_PENDING: readonly PortalStatus[] = ["invited", "submitted", "sent_back"];
/** Statuses in which the family may change the record directly (before the school's check). */
export const PORTAL_EDITABLE: readonly PortalStatus[] = ["invited", "sent_back"];

const loginAccessSchema = z.discriminatedUnion(
  "method",
  [
    z.object({ method: z.literal("link") }),
    z.object({
      method: z.literal("password"),
      password: newPasswordSchema,
      /** The student's own, when they get a sign-in too. */
      studentPassword: newPasswordSchema.optional(),
    }),
  ],
  { error: "Choose how they get their password" },
);

/** The office gives a family (and, from the school's chosen class, the student) a sign-in. */
export const portalInviteSchema = z.object({
  /** Who gets the parent sign-in: the main contact unless the office picks someone else. */
  personId: personIdSchema,
  access: loginAccessSchema,
  /**
   * True: the family checks and completes the record first, and the school approves it before
   * the app opens. False: the app opens straight away (a record the office already checked).
   */
  checkFirst: z.boolean().default(true),
  /** The student's own sign-in, when their class is one the school gives them from. */
  studentLogin: z.boolean().default(false),
});
export type PortalInviteInput = z.input<typeof portalInviteSchema>;
export type PortalInvite = z.output<typeof portalInviteSchema>;

export interface PortalInviteResult {
  parentLoginId: string;
  studentLoginId: string | null;
  /** Null when no email was sent (a temporary password); false when sending failed. */
  emailSent: boolean | null;
}

/** The school's decision on what a family sent. */
export const portalReviewSchema = z
  .object({
    decision: z.enum(["approve", "send_back"], { error: "Choose approve or send back" }),
    note: z
      .string()
      .trim()
      .max(300, { error: "Note is too long" })
      .transform((value) => (value === "" ? null : value))
      .nullable()
      .optional(),
    /** The student's version, so a decision isn't made on a record that changed meanwhile. */
    version: z.number().int().positive(),
  })
  .superRefine((review, ctx) => {
    if (review.decision === "send_back" && !review.note) {
      ctx.addIssue({
        code: "custom",
        path: ["note"],
        message: "Say what the family needs to fix",
      });
    }
  });

/** What a family saves while completing a record (only what the school lets parents change). */
export const familyDetailsSchema = z
  .object({
    details: z.record(z.string(), z.unknown()).optional(),
    custom: z.record(z.string(), z.unknown()).optional(),
    version: z.number().int().positive(),
  })
  .refine((value) => value.details !== undefined || value.custom !== undefined, {
    error: "Change at least one detail",
  });

/** A family asks the school to change details or add someone, after the app has opened. */
export const changeRequestSchema = z
  .object({
    details: z.record(z.string(), z.unknown()).default({}),
    custom: z.record(z.string(), z.unknown()).default({}),
    /** New people (an emergency contact, someone to pick the student up); never the main contact. */
    people: z
      .array(personSchema)
      .max(3, { error: "Ask for at most 3 people at a time" })
      .default([]),
  })
  .superRefine((request, ctx) => {
    const count =
      Object.keys(request.details).length +
      Object.keys(request.custom).length +
      request.people.length;
    if (count === 0) ctx.addIssue({ code: "custom", message: "Change at least one detail" });
    for (const [index, person] of request.people.entries()) {
      if (person.id) {
        ctx.addIssue({ code: "custom", path: ["people", index], message: "Add a new person" });
      }
      if (person.isPrimary) {
        ctx.addIssue({
          code: "custom",
          path: ["people", index, "isPrimary"],
          message: "Only the school can change the main contact",
        });
      }
    }
  });
export type ChangeRequestInput = z.input<typeof changeRequestSchema>;

export const changeReviewSchema = z
  .object({
    decision: z.enum(["approved", "rejected"], { error: "Choose approve or reject" }),
    note: z
      .string()
      .trim()
      .max(300, { error: "Note is too long" })
      .transform((value) => (value === "" ? null : value))
      .nullable()
      .optional(),
    version: z.number().int().positive(),
  })
  .superRefine((review, ctx) => {
    if (review.decision === "rejected" && !review.note) {
      ctx.addIssue({
        code: "custom",
        path: ["note"],
        message: "Say why, so the family knows what to do",
      });
    }
  });

export const CHANGE_REQUEST_STATUSES = ["pending", "approved", "rejected"] as const;
export type ChangeRequestStatus = (typeof CHANGE_REQUEST_STATUSES)[number];

export interface ChangeRequestDto {
  id: string;
  requestedBy: string | null;
  requestedAt: string;
  status: ChangeRequestStatus;
  /** What they asked for, as far as the viewer may see it ("allergies", "Bus route"). */
  details: DetailValues;
  custom: DetailValues;
  people: { name: string; relation: PersonRelation; isEmergency: boolean; canPickUp: boolean }[];
  /** True when some of it is hidden from the viewer (private or health details). */
  hiddenParts: boolean;
  reviewNote: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  version: number;
}

/** The family side of a student, for the office and the family. */
export interface StudentPortalDto {
  status: PortalStatus;
  /** The school's note when it sent the record back. */
  note: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  /** Family sign-ins linked to this student, by User ID. */
  logins: { userId: string; loginId: string; name: string; kind: "parent" | "student" }[];
  /** Whether this student's class is one the school gives students their own sign-in from. */
  studentLoginOffered: boolean;
  pendingChanges: number;
}

// ---------------------------------------------------------------------------------------------
// Online admission: the public form families fill in, and the office's decision
// ---------------------------------------------------------------------------------------------

export const APPLICATION_STATUSES = ["submitted", "admitted", "rejected"] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  submitted: "Waiting for a decision",
  admitted: "Admitted",
  rejected: "Not admitted",
};

/** Application numbers: their own series each year, apart from admission numbers. */
export const APPLICATION_NUMBER_FORMAT = "APP/{YYYY}/{SEQ:4}";

/**
 * The form as the public may see it: each field's setting as the online form uses it (off
 * unless it's on the form, or asked about each person), the school's own questions for the
 * form, documents and family rules. Nothing else of the school's set-up (number format,
 * family app, other fields' settings) leaves the school.
 */
export function settingsForPublic(settings: AdmissionSettings): AdmissionSettings {
  const catalogue: readonly FieldDef[] = FIELD_CATALOGUE;
  const fields = Object.fromEntries(
    catalogue.map((field) => {
      const setting = fieldSetting(settings, field);
      const asked = field.perPerson ? setting.enabled : setting.publicForm;
      return [
        field.key,
        asked ? setting : { ...setting, enabled: false, required: false, publicForm: false },
      ];
    }),
  );
  return {
    ...DEFAULT_ADMISSION_SETTINGS,
    fields,
    customFields: settings.customFields.filter(
      (field) => field.status === "active" && field.publicForm,
    ),
    documents: settings.documents,
    customDocuments: settings.customDocuments.filter((doc) => doc.status === "active"),
    photo: settings.photo,
    family: settings.family,
  };
}

/**
 * What a family agrees to when sending an application (DPDP Act). The version is stored with
 * each application, so a change of wording needs a new version.
 */
export const APPLICATION_CONSENT_VERSION = "2026-11-04";
export function applicationConsentText(schoolName: string): string {
  return `I'm the child's parent or guardian, and I agree to ${schoolName} using these details for admission`;
}

export const applyEmailSchema = z.object({ email: emailSchema });

const emailCheckIdSchema = z.string().regex(/^aec_[0-9A-Z]{26}$/, { error: "Ask for a new code" });

export const applyCodeSchema = z.object({
  checkId: emailCheckIdSchema,
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, { error: "Enter the 6-digit code from the email" }),
});

const documentIdSchema = z.string().regex(/^sdc_[0-9A-Z]{26}$/, { error: "Unknown document" });

/** What a family sends from the public page. Details are checked with the school's settings. */
export const publicApplicationSchema = z.object({
  classId: z.string().trim().min(1, { error: "Choose the class" }).max(64),
  firstName: studentCoreFields.shape.firstName,
  lastName: studentCoreFields.shape.lastName.default(""),
  dateOfBirth: studentCoreFields.shape.dateOfBirth,
  gender: studentCoreFields.shape.gender,
  details: z.record(z.string(), z.unknown()).default({}),
  custom: z.record(z.string(), z.unknown()).default({}),
  people: peopleSchema,
  documentIds: z.array(documentIdSchema).max(20).default([]),
  /** The family agrees to the school using what they send for admission (DPDP Act). */
  consent: z.literal(true, { error: "Tick this to send the application" }),
});
export type PublicApplicationInput = z.input<typeof publicApplicationSchema>;
export type PublicApplication = z.output<typeof publicApplicationSchema>;

/** The office admits an application: where the student goes and their number. */
export const admitApplicationSchema = z.object({
  sectionId: studentCoreFields.shape.sectionId,
  admissionNo: z.preprocess(
    (value) => (typeof value === "string" && value.trim() === "" ? undefined : value),
    studentCoreFields.shape.admissionNo.optional(),
  ),
  admissionDate: z.iso.date({ error: "Enter the admission date as YYYY-MM-DD" }).optional(),
  /** Applied: the student is made but details the school requires can follow. */
  status: z
    .enum(["applied", "admitted", "active"], { error: "Choose a status" })
    .default("admitted"),
  version: z.number().int().positive(),
});
export type AdmitApplicationInput = z.input<typeof admitApplicationSchema>;

export const rejectApplicationSchema = z.object({
  note: z
    .string()
    .trim()
    .min(1, { error: "Say why, so the family knows" })
    .max(300, { error: "Note is too long" }),
  version: z.number().int().positive(),
});

export const APPLICATION_SORTS = ["submittedAt", "name", "applicationNo"] as const;

export const applicationListQuerySchema = z.object({
  cursor: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  q: z.string().trim().max(80).optional(),
  status: z.enum(APPLICATION_STATUSES).optional(),
  classId: z.string().trim().max(64).optional(),
  sort: z
    .enum([...APPLICATION_SORTS, ...APPLICATION_SORTS.map((key) => `-${key}` as const)])
    .default("-submittedAt"),
});
export type ApplicationListQuery = z.output<typeof applicationListQuerySchema>;

/** The public page before anything is sent: the school and what it asks. */
export interface PublicAdmissionDto {
  schoolName: string;
  institutionCode: string;
  intro: string | null;
  mode: "application" | "enquiry";
  classes: { id: string; name: string }[];
  settings: AdmissionSettings;
}

/** An application sent: its number, and whether the receipt email went. */
export interface ApplicationSentDto {
  applicationNo: string;
  emailSent: boolean;
}

export interface ApplySessionDto {
  /** `<check id>.<secret>`, sent back as `x-application-session`. Kept in memory only. */
  session: string;
  expiresIn: number;
  email: string;
}

export interface ApplicationListItemDto {
  id: string;
  applicationNo: string;
  kind: "application" | "enquiry";
  status: ApplicationStatus;
  fullName: string;
  dateOfBirth: string;
  className: string;
  submittedAt: string;
}

export interface ApplicationDto extends ApplicationListItemDto {
  firstName: string;
  lastName: string;
  gender: StudentDto["gender"];
  /** The address the family proved; null for people who may not see contact details. */
  email: string | null;
  classId: string;
  details: DetailValues;
  custom: DetailValues;
  people: {
    name: string;
    relation: PersonRelation;
    mobile: string;
    email: string | null;
    isPrimary: boolean;
    isEmergency: boolean;
    canPickUp: boolean;
  }[];
  documents: {
    id: string;
    kind: string;
    label: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
    uploadedAt: string;
  }[];
  /** Students already on the roll with the same name and date of birth. */
  duplicates: DuplicateMatchDto[];
  /** What the person's role keeps from them (left out of the answer, not just masked). */
  hidden: { sensitive: boolean; health: boolean; contact: boolean; documents: boolean };
  decisionNote: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  studentId: string | null;
  version: number;
}
